import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowSpace", "aria-describedby", "delayInputErrors", "error", "errorIcon", "errorRef", "inputErrorProps", "inputRef", "onBlur", "onChange", "pretty", "required", "validate", "value"];
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { getErrorMessage } from '@op/opds.validate';
import { useFocus } from '../common/hooks';
import { useLocale } from '../common/locale';
import { FormRowContext } from '../form-row/form-row';
import TextInput from '../text-input/text-input';
import InputError from '../input-error/input-error';

// Add alias for merging ids, since classNames does the job well
const mergeIds = classNames;
function getNextError(eventValueIsInvalid, delay, validated, required, eventValue, errorEmpty) {
  if (eventValueIsInvalid) {
    return delay || validated;
  }
  return required && !eventValue ? errorEmpty() : null;
}
const messages = {
  empty: {
    en: getErrorMessage('en', 'empty'),
    fi: getErrorMessage('fi', 'empty'),
    sv: getErrorMessage('sv', 'empty')
  }
};
class ErrorWithCode extends Error {
  constructor() {
    super(...arguments);
    this.code = void 0;
  }
}

/**
 * _A field that validates and prettifies information entered by users._
 *
 * ***
 *
 *
 * A controlled input component that validates and prettifies its input value.
 * To use, the `onChange` and `value` props must be set.
 *
 * The component wraps and extends [TextInput](#/Forms/TextInput) and will
 * accept most of its props. In addition to enabling `validate` and `pretty`
 * functions to control the input value, it includes an optional `error`
 * indicator as well.
 *
 * The validator and prettifier functions provided by
 * [@op/opds.validate](https://oppci.eficode.com/site/docs/@op/opds.validate/latest/) may be used directly by this component.
 *
 * Props that are not explicitly listed below will be passed to
 * [TextInput](#/Forms/TextInput) and from there to the HTML `<input>` or
 * `<textarea>` element. In particular you may want to set
 * [`inputmode` (developer.mozilla.org)](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode)
 * for better mobile support.
 */
export default function ValidInput(_ref) {
  let {
      // Should this possibly be removed altogether if it's not used anyway?
      // discarded; is included in some @op/opds.validate objects
      'aria-describedby': prevDesc,
      delayInputErrors = true,
      error,
      errorIcon,
      errorRef,
      inputErrorProps,
      inputRef: propRef,
      onBlur,
      onChange,
      pretty = value => value,
      required: requiredProp,
      validate = value => value,
      value: propValue
    } = _ref,
    inputProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const lc = useLocale();
  const [value, setValue] = useState('');
  const [errorId] = useState("ds-ti-err-" + uuidv4());
  const internalRef = useRef(null);
  const valueRef = useRef('');
  const {
    required: requiredContext
  } = useContext(FormRowContext);
  const inputRef = propRef && typeof propRef !== 'function' ? propRef : internalRef;
  const hasFocus = useFocus(inputRef, undefined);
  const required = requiredProp != null ? requiredProp : requiredContext;
  const [prettifyValues, setPrettifyValues] = useState(false);
  const valueHasChanged = pretty(propValue, true) !== value;
  const setPrettifiedValue = stringToPretty => {
    setValue(pretty(stringToPretty, true));
  };
  useEffect(() => {
    if (!propValue) {
      setValue('');
    } else if (valueHasChanged && prettifyValues) {
      setPrettifiedValue(propValue);
    } else if (prettifyValues) {
      setPrettifiedValue(propValue);
    } else {
      setValue(propValue);
    }
  }, [propValue]);
  useEffect(() => {
    if (valueHasChanged) {
      setPrettifiedValue(propValue);
    }
  }, [hasFocus]);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  const errorEmpty = () => {
    const errorWithCode = new ErrorWithCode(required instanceof Error ? required.message : messages.empty[lc]);
    errorWithCode.code = 'empty';
    return errorWithCode;
  };
  const handleChange = useCallback(event => {
    setPrettifyValues(false);
    const {
      value: eventValue
    } = event.target;
    const validated = eventValue && validate(pretty(eventValue));
    const prevValidated = valueRef.current && validate(pretty(valueRef.current));
    const eventValueIsInvalid = validated instanceof Error;

    // While input is happening, don't show some error messages
    // until focus moves elsewhere or if we're already showing
    // an error.
    const delayableError = eventValueIsInvalid && (delayInputErrors === true || Array.isArray(delayInputErrors) && validated.code && delayInputErrors.indexOf(validated.code) !== -1);
    const firstError = !error;
    const alreadyDelayed = error === true;
    const errorMessageChanged = prevValidated instanceof Error && validated instanceof Error && prevValidated.code !== validated.code;
    const delay = delayableError && event.type === 'change' && (firstError || alreadyDelayed || errorMessageChanged);

    // `nextError` is meant to be used as a value that can be fed back
    // as our `error` prop, so `null` indicates no error, `true` is used
    // during input when e.g. not enough chars have been entered, and an
    // `Error` is for invalid input values.
    const nextError = getNextError(eventValueIsInvalid, delay, validated, required, eventValue, errorEmpty);

    // For valid input, switch to the validated value on blur
    const nextValue = !eventValueIsInvalid && event.type === 'blur' ? validated : eventValue;
    const prettyValue = pretty(nextValue, true);
    const usePrettyOrNextValue = !!prettyValue && prettyValue.replaceAll(' ', '').startsWith(nextValue.replaceAll(' ', ''));
    if (event.type === 'blur') {
      setValue(prettyValue);
    } else {
      setValue(usePrettyOrNextValue ? nextValue : prettyValue);
    }
    onChange(event, nextValue, nextError, validated);
  }, [error, onChange, pretty, required, validate]);
  const handleBlur = useCallback(event => {
    handleChange(event);
    setPrettifyValues(true);
    onBlur == null || onBlur(event);
  }, [handleChange, onBlur]);
  const valid = value ? validate(value) : true;
  const invalid = !!error || valid instanceof Error;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, _extends({}, inputProps, {
    "aria-describedby": mergeIds({
      [errorId]: error
    }, prevDesc) || undefined,
    hasFocus: hasFocus,
    invalid: invalid,
    onBlur: handleBlur,
    onChange: handleChange,
    ref: inputRef,
    required: !!required,
    value: value
  })), /*#__PURE__*/React.createElement(InputError, _extends({
    "aria-live": "off"
  }, inputErrorProps, {
    error: typeof error !== 'boolean' && (error instanceof Error ? error.message : error),
    icon: errorIcon,
    id: errorId,
    ref: errorRef,
    success: !invalid
  })));
}
ValidInput.displayName = 'ValidInput';